import { useCallback, useMemo } from 'react'
import { isEqual } from 'lodash'
import { Layer } from '@carto/kepler.gl/layers'
import { useAppDispatch, useAppSelector } from 'app/state'
import { RootState, runAppSaga } from 'app/state/store'
import useBuilderMode from 'features/builder/hooks/useBuilderMode'
import { Connection, ProviderType } from 'features/connections'
import { getDataExplorerObjectQuery } from 'features/data-explorer/api/dataExplorerQueries'
import { DataExplorerTable } from 'features/data-explorer/types'
import {
  BoundSqlParameter,
  Map,
  MapDataset,
  MapType,
  SqlParameter,
  SqlParameterType,
  SqlCategoryParameter
} from 'features/maps/types'
import { getSQLQueryInfo } from 'features/sql/api/sqlQueries'
import { SQLResponse } from 'features/sql/types'
import { useIsMutating, useMutation, useQuery } from 'react-query'
import { getAttributeStats, saveCustomMarkerIcon, GetAttributeStatsCategoryOrder } from '../api/builder'
import { switchLayerTypeWithDatasetChange } from '../state/builderSagas'
import {
  addSqlParameter,
  removeSqlParameter,
  setControlsPaneState,
  setMapScreenshot,
  setTakeMapScreenshots,
  updateDatasetsWithSqlParametersRequest,
  updateSqlParameter
} from '../state/builderSlice'
import { AttributeStats, KeplerLayer, LayerType, LayerTypes, RightPaneTab } from '../types'
import { sqlParameterItems } from '../utils/queryParametersUtils'
import { sendMetricEvent } from 'features/metrics/model/metricsMutations'
import { MetricEventType } from 'features/metrics/api/metrics'
import { createSelector } from '@reduxjs/toolkit'
import { builderModeToMapPrivacy } from '../utils/types'
import { getClient } from 'features/common/utils/clientParameter'
import { updateMapSettings } from 'features/maps/state/mapsSlice'
import { MapStyleType } from '../ui/KeplerGl/types'
import { RequestStatus } from 'features/common'
import { publishAndSetMapPrivacy, updateMapAndPublish } from '../model/sharing'
import { lockMap } from 'features/maps/api/maps'
import { isInBuilder } from '../paths'
import { isMapSplit } from '../state/keplerGlSlice'
import { MapState } from '@carto/kepler.gl/reducers'

export function useDragMapState(mainMapState: MapState) {
  const dragMapState = useAppSelector((state) => state.builder.dragMapState)
  return useMemo(() => {
    if (!dragMapState) return mainMapState

    return { ...mainMapState, ...dragMapState }
  }, [mainMapState, dragMapState])
}

export function useSourceRegion(mapDataset: MapDataset, connection: Connection) {
  const queryOptions =
    mapDataset.type === MapType.table
      ? getDataExplorerObjectQuery(mapDataset.source, connection.id)
      : getSQLQueryInfo({
          query: mapDataset.source,
          queryParameters: mapDataset.queryParameters,
          connectionId: connection.name
        })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = useQuery<any>(queryOptions)

  if (!data) return {}

  let region
  if (mapDataset.type === MapType.table) {
    region = (data as DataExplorerTable).tableRegion
  } else if (mapDataset.type === MapType.query) {
    region = (data as SQLResponse).meta.location
  }

  return { data: region }
}

export function useSaveMarkerMutation() {
  return useMutation({
    mutationFn: saveCustomMarkerIcon
  })
}

export function usePublishAndSetPrivacyMutation() {
  return useMutation({
    mutationFn: publishAndSetMapPrivacy
  })
}

export function useUpdateMapAndPublish() {
  return useMutation({
    mutationFn: async (
      partialMap: Partial<Map> & {
        id: Map['id']
      }
    ) => {
      const response = await updateMapAndPublish(partialMap)
      if (partialMap.collaborative && isInBuilder()) {
        await lockMap({ mapId: partialMap.id })
      }
      return response
    }
  })
}

const switchLayerTypeMutationKey = `builder/switchLayerTypeWithDatasetChange`

export function useSwitchLayerTypeWithDatasetChange(layerId) {
  return useMutation({
    mutationKey: [switchLayerTypeMutationKey, layerId],
    mutationFn: async ({ newLayerType }: { newLayerType: LayerType }) => {
      await runAppSaga(function* () {
        yield switchLayerTypeWithDatasetChange({ layerId, newLayerType })
      })
    }
  })
}

export function useIsSwitchingLayerType(layerId) {
  return (
    useIsMutating({
      mutationKey: [switchLayerTypeMutationKey, layerId]
    }) > 0
  )
}

const unsupportedSqlParameterProviders = [ProviderType.DATABRICKS, ProviderType.DATABRICKS_REST]

const addSqlParameterEnabledSelector = createSelector(
  (state: RootState) => state.maps.currentMapDatasets,
  (state: RootState) => state.builder.sqlPanel,
  (currentMapDatasets, sqlPanel) =>
    currentMapDatasets.some(
      (d) => !unsupportedSqlParameterProviders.includes(d.providerId) && d.type === MapType.query
    ) ||
    Boolean(
      sqlPanel &&
        !sqlPanel.minimized &&
        (!sqlPanel.provider || !unsupportedSqlParameterProviders.includes(sqlPanel.provider))
    )
)

export function useAddSqlParameterAction() {
  const dispatch = useAppDispatch()
  const mapId = useAppSelector((state) => state.maps.currentMap?.id || '')
  const mapSettings = useAppSelector((state) => state.maps.mapSettings)
  const enabled = useAppSelector(addSqlParameterEnabledSelector)

  const createSqlParameter = (parameter: SqlParameter) => {
    dispatch(addSqlParameter(parameter))
    dispatch(setControlsPaneState({ open: true, tab: RightPaneTab.Parameters }))
    dispatch(updateDatasetsWithSqlParametersRequest({ parameter }))
    if (mapSettings.sqlParameterControls === undefined) {
      dispatch(updateMapSettings({ sqlParameterControls: true }))
    }

    sendMetricEvent(MetricEventType.SQLParameterCreated, {
      type: parameter.type,
      mapId,
      ...(parameter.type === SqlParameterType.Category
        ? { orderBy: (parameter as SqlCategoryParameter).orderBy }
        : undefined)
    })
  }

  return {
    createSqlParameter,
    enabled
  }
}

export function useSqlParameterListActions({ sqlParameters }: { sqlParameters: BoundSqlParameter[] }) {
  const dispatch = useAppDispatch()
  const { shouldSaveMap, isEditorMode } = useBuilderMode()
  const mapId = useAppSelector((state) => state.maps.currentMap?.id || '')
  const builderMode = useAppSelector((state) => state.builder.builderMode)

  const handleChange = (newParameter: SqlParameter) => {
    dispatch(updateSqlParameter(newParameter))

    const newItems = Array.from(sqlParameterItems([newParameter]))

    const oldParameter = sqlParameters.find((p) => p.id === newParameter.id)
    const oldItems = oldParameter ? Array.from(sqlParameterItems([oldParameter])) : []
    if (!isEqual(oldItems, newItems)) {
      dispatch(updateDatasetsWithSqlParametersRequest({ parameter: newParameter, skipSaveDataset: !shouldSaveMap }))

      sendMetricEvent(MetricEventType.SQLParameterUsed, {
        type: newParameter.type,
        mapId,
        mapPrivacy: builderModeToMapPrivacy(builderMode)
      })
    }
  }
  const handleRemove = (removedParameter: SqlParameter) => {
    dispatch(removeSqlParameter(removedParameter))
    dispatch(updateDatasetsWithSqlParametersRequest({}))
  }
  return {
    readonly: !isEditorMode,
    handleChange,
    handleRemove
  }
}

export function useDatasetAttributeStatsQuery({
  dataset,
  column = '',
  maxNumberOfCategories,
  categoriesOrderBy,
  onSuccess,
  noCache
}: {
  dataset?: MapDataset
  column?: string
  maxNumberOfCategories?: number
  categoriesOrderBy?: GetAttributeStatsCategoryOrder
  onSuccess?: (data: AttributeStats) => void
  noCache?: boolean
}) {
  const enabled = Boolean(column && dataset)
  const {
    connectionName: connection = '',
    source = '',
    queryParameters,
    type,
    accessToken
  } = dataset || ({} as MapDataset)
  return useQuery({
    enabled: enabled,
    queryKey: ['builder/getAttributeStatsQuery', connection, source, column, queryParameters, categoriesOrderBy],
    queryFn: ({ signal }) =>
      getAttributeStats({
        connection,
        accessToken,
        source,
        queryParameters,
        attribute: column,
        maxNumberOfCategories,
        categoriesOrderBy,
        type,
        signal,
        client: getClient(),
        noCache
      }),
    onSuccess
  })
}

export function useTakeMapScreenshots() {
  const dispatch = useAppDispatch()
  const takeMapScreenshots = useAppSelector((state) => state.builder.takeMapScreenshots)
  const mapScreenshots = useAppSelector((state) => state.builder.mapScreenshots)
  const layers: Layer[] = useAppSelector((state) => state.keplerGl.map.visState.layers)
  const isSplit = useAppSelector(isMapSplit)
  const mapStyle = useAppSelector((state) => state.keplerGl?.map?.mapStyle?.bottomMapStyle)
  const currentMapDatasets = useAppSelector((state) => state.maps.currentMapDatasets)
  const isLoading = takeMapScreenshots

  const readyForScreenshot = useMemo(() => {
    if (!takeMapScreenshots) return false
    if (currentMapDatasets.some((dataset) => dataset.status === RequestStatus.Pending)) return false

    const tileLayersFullyLoaded = layers.every(
      (layer) =>
        layer.type !== LayerTypes.Tileset ||
        (layer.type === LayerTypes.Tileset && !(layer as KeplerLayer).isLoadingTiles) ||
        !layer.config.isVisible
    )

    return tileLayersFullyLoaded
  }, [currentMapDatasets, layers, takeMapScreenshots])

  const requireNewScreenshots = useCallback(() => {
    dispatch(setTakeMapScreenshots(true))
  }, [dispatch])

  const takeMapScreenshot = (mapContainerRef: HTMLDivElement, primary: boolean) => {
    if (!readyForScreenshot) return
    let screenshot
    const index = isSplit ? (primary ? 1 : 0) : 0

    if (mapStyle?.type === MapStyleType.GOOGLE_MAPS) {
      const googleCanvas = mapContainerRef.querySelector('canvas')
      if (!googleCanvas) return
      screenshot = googleCanvas.toDataURL('image/png')
    } else {
      const basemapCanvas: HTMLCanvasElement | null = mapContainerRef.querySelector('.maplibregl-canvas')
      const deckCanvas: HTMLCanvasElement | null = mapContainerRef.querySelector('#default-deckgl-overlay')

      if (!basemapCanvas || !deckCanvas) return
      const canvas = document.createElement('canvas')
      canvas.width = basemapCanvas.width
      canvas.height = basemapCanvas.height
      const ctx = canvas.getContext('2d')
      ctx?.drawImage(basemapCanvas, 0, 0)
      ctx?.drawImage(deckCanvas, 0, 0)
      screenshot = canvas.toDataURL('image/png')
    }

    dispatch(setMapScreenshot({ index, screenshot }))

    if (!isSplit || mapScreenshots[index === 0 ? 1 : 0] !== undefined) {
      dispatch(setTakeMapScreenshots(false))
    }
  }

  return { takeMapScreenshot, readyForScreenshot, isLoading, requireNewScreenshots, mapScreenshots }
}
